/**
 * §B-5 legal-entity (importer-of-record) editor — Drawer form. Collects the full
 * IOR identity the carriers need at customs time (§10.2): legal name, a complete
 * postal address, contact (person/phone/email), and the customs registration ids
 * (EORI/VAT/EIN/CRA). Replaces the old raw "Address JSON" textarea — the address
 * is now structured `Address` fields (street1/…/country), matching what
 * buildShipmentInput reads. Saves via the ADMIN API with optimistic concurrency.
 */
import { useState } from "react";
import { ApiError, api, type AdminLegalEntity, type LegalEntityInput } from "../../../lib/api.js";
import { countryOptions } from "../../../lib/countries.js";
import { Drawer, Field, Input, Select, Button, useToast } from "../../../components/ui/index.js";

/** Read a value from a stored addressJson, tolerating the legacy `line1` key. */
function addr(json: unknown, ...keys: string[]): string {
  if (!json || typeof json !== "object") return "";
  const o = json as Record<string, unknown>;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return "";
}

export function LegalEntityEditor({ entity, onClose, onSaved }: { entity: AdminLegalEntity | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isNew = entity === null;
  const a = entity?.addressJson;

  const [name, setName] = useState(entity?.name ?? "");
  const [contactName, setContactName] = useState(addr(a, "name"));
  const [street1, setStreet1] = useState(addr(a, "street1", "line1"));
  const [street2, setStreet2] = useState(addr(a, "street2", "line2"));
  const [city, setCity] = useState(addr(a, "city"));
  const [province, setProvince] = useState(addr(a, "province", "state"));
  const [postcode, setPostcode] = useState(addr(a, "postcode", "zip"));
  // Uppercase a 2-letter code so a legacy lowercase value ("de") preselects in the
  // dropdown (options are uppercase ISO-2); a stored full name stays as-is and is
  // forced to be reselected on save.
  const [country, setCountry] = useState(() => {
    const c = addr(a, "country");
    return /^[a-z]{2}$/i.test(c) ? c.toUpperCase() : c;
  });
  const [phone, setPhone] = useState(addr(a, "phone"));
  const [email, setEmail] = useState(addr(a, "email"));
  const [eori, setEori] = useState(entity?.eori ?? "");
  const [vatNumber, setVat] = useState(entity?.vatNumber ?? "");
  const [ein, setEin] = useState(entity?.ein ?? "");
  const [craNumber, setCra] = useState(entity?.craNumber ?? "");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return toast("Legal name is required.", "danger");
    if (!street1.trim() || !city.trim()) return toast("Street address and city are required.", "danger");
    if (!/^[A-Za-z]{2}$/.test(country.trim())) return toast("A 2-letter country is required.", "danger");

    // Build the canonical Address shape (drop blanks). company = the legal name;
    // name = the contact/attention line (falls back to the legal name).
    const addressJson: Record<string, unknown> = { company: name.trim(), country: country.trim().toUpperCase() };
    const put = (k: string, v: string) => { if (v.trim()) addressJson[k] = v.trim(); };
    put("name", contactName || name);
    put("street1", street1);
    put("street2", street2);
    put("city", city);
    put("province", province);
    put("postcode", postcode);
    put("phone", phone);
    put("email", email);

    const input: LegalEntityInput = {
      name: name.trim(),
      addressJson,
      eori: eori.trim() || null,
      vatNumber: vatNumber.trim() || null,
      ein: ein.trim() || null,
      craNumber: craNumber.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) await api.admin.createLegalEntity(input);
      else await api.admin.updateLegalEntity(entity.id, input, entity.updatedAt);
      toast(isNew ? "Entity created" : "Entity saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("This entity changed since you opened it — reloading.", "danger");
        if (!isNew) onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid entity.", "danger");
      } else {
        toast("Couldn’t save the entity.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer onClose={onClose} ariaLabel={isNew ? "New legal entity" : `Edit ${entity.name}`} title={isNew ? "New importer entity" : "Edit importer entity"} meta={<span className="muted">Importer-of-record details carriers use at customs.</span>}>
      <div className="rule-form">
        <fieldset className="secret-group">
          <legend>Identity</legend>
          <Field label="Legal name" hint="The importing company on the customs declaration.">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Alpine Nation US LLC" autoFocus />
          </Field>
          <Field label="Contact name" hint="Attention / customs contact (optional).">
            <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Trade Compliance" />
          </Field>
        </fieldset>

        <fieldset className="secret-group">
          <legend>Address</legend>
          <Field label="Street address">
            <Input value={street1} onChange={(e) => setStreet1(e.target.value)} placeholder="1 Mountain Way" />
          </Field>
          <Field label="Apt / suite / unit">
            <Input value={street2} onChange={(e) => setStreet2(e.target.value)} placeholder="Suite 200 (optional)" />
          </Field>
          <div className="rule-form__row2">
            <Field label="City">
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Denver" />
            </Field>
            <Field label="State / province">
              <Input value={province} onChange={(e) => setProvince(e.target.value)} placeholder="CO" />
            </Field>
          </div>
          <div className="rule-form__row2">
            <Field label="Postcode">
              <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="80202" />
            </Field>
            <Field label="Country">
              <Select ariaLabel="Country" value={country} onChange={setCountry} searchable options={[{ value: "", label: "Select…" }, ...countryOptions()]} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="secret-group">
          <legend>Contact</legend>
          <div className="rule-form__row2">
            <Field label="Phone">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 303 555 0100" />
            </Field>
            <Field label="Email">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customs@alpinenation.us" />
            </Field>
          </div>
        </fieldset>

        <fieldset className="secret-group">
          <legend>Customs registration</legend>
          <div className="rule-form__row2">
            <Field label="EORI">
              <Input value={eori} onChange={(e) => setEori(e.target.value)} placeholder="DE123456789" />
            </Field>
            <Field label="VAT number">
              <Input value={vatNumber} onChange={(e) => setVat(e.target.value)} placeholder="DE123456789" />
            </Field>
          </div>
          <div className="rule-form__row2">
            <Field label="EIN (US)">
              <Input value={ein} onChange={(e) => setEin(e.target.value)} placeholder="12-3456789" />
            </Field>
            <Field label="CRA (CA)">
              <Input value={craNumber} onChange={(e) => setCra(e.target.value)} placeholder="123456789RM0001" />
            </Field>
          </div>
        </fieldset>

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create entity" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
